import React, { createContext, useContext } from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../services/paginatedList';

export interface VesselSparePendingPurchaseOrderRow {
  id: number;
  orderDate?: string | null;
  ref?: string | null;
  caseId: number;
  supplierId: number;
  quotationId: number;
  pendingLineCount: number;
  totalPendingQty: number;
  lineItemCount: number;
  spareCase?: {
    id: number;
    ref?: string | null;
    vesselId?: number | null;
    caseDate?: string | null;
    eta?: string | null;
    vessel?: { id: number; vesselName?: string | null; vesselAbrv?: string | null } | null;
    port?: { name: string; code?: string | null } | null;
  } | null;
  quotation?: { id: number; ref?: string | null; quoDate?: string | null } | null;
  supplier?: { id: number; name: string; code?: string | null; email?: string | null; isActive?: boolean } | null;
}

export interface VesselSparePendingPurchaseOrderLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  unitId?: number | null;
  orderedQty: number;
  deliveredQty: number;
  pendingQty: number;
  itemNotes?: string | null;
  item?: { level1?: string | null; level2?: string | null; level3?: string | null } | null;
  unit?: { id?: number; ucode?: string | null; descr?: string | null } | null;
}

export interface VesselSparePendingPurchaseOrderDetail {
  id: number;
  orderDate?: string | null;
  ref?: string | null;
  caseId: number;
  quotationId: number;
  spareCase?: VesselSparePendingPurchaseOrderRow['spareCase'];
  quotation?: VesselSparePendingPurchaseOrderRow['quotation'];
  supplier?: VesselSparePendingPurchaseOrderRow['supplier'];
  lineItems: VesselSparePendingPurchaseOrderLine[];
}

const VesselSparePendingDeliveriesListContext = createContext<number | null>(null);

export function VesselSparePendingDeliveriesListProvider(props: { vesselId: number; children: React.ReactNode }) {
  return React.createElement(VesselSparePendingDeliveriesListContext.Provider, { value: props.vesselId }, props.children);
}

function buildPendingForVesselListQuery(
  vesselId: number,
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
): string {
  const rest = buildListQuery(searchParams, page, limit, options);
  const params = new URLSearchParams(rest.startsWith('?') ? rest.slice(1) : rest);
  params.set('vesselId', String(vesselId));
  return `?${params.toString()}`;
}

export function useVesselSparePendingDeliveriesListData(
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) {
  const vesselId = useContext(VesselSparePendingDeliveriesListContext);
  const q =
    vesselId != null && Number.isFinite(vesselId)
      ? buildPendingForVesselListQuery(vesselId, searchParams, page, limit, options)
      : null;
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<VesselSparePendingPurchaseOrderRow> | VesselSparePendingPurchaseOrderRow[]
  >(q ? `/spare-purchase-orders/pending-for-vessel${q}` : null, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<VesselSparePendingPurchaseOrderRow>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
}

export function useVesselSparePendingPurchaseOrderDetail(purchaseOrderId: number | undefined, vesselId: number | undefined) {
  const key =
    purchaseOrderId != null &&
    vesselId != null &&
    Number.isFinite(purchaseOrderId) &&
    Number.isFinite(vesselId)
      ? `/spare-purchase-orders/pending-for-vessel/${purchaseOrderId}?vesselId=${vesselId}`
      : null;
  const { data, error, isLoading, mutate } = useSWR<VesselSparePendingPurchaseOrderDetail>(key, fetcher, {
    revalidateOnFocus: true,
  });
  return { data, item: data, isLoading, isError: error, mutate };
}
